import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { fetchChatResponse } from './api';
import { searchDocuments, loadPreprocessedData } from './vectorStore';
import PDFUploader from './PDFUploader';
import { IoSend, IoDocumentAttach, IoInformation, IoMenuSharp, IoLogOut, IoImages, IoChevronForward, IoMic } from 'react-icons/io5';
import MessageContent from './MessageContent';
import { extractedImages } from './documentProcessor';
import { createClient } from '@supabase/supabase-js';
import LoginPage from './LoginPage';
import LoadingScreen from './LoadingScreen';
import VoiceCallButton from './components/VoiceCallButton';
import VoiceCallOverlay from './components/VoiceCallOverlay';

// Import styles for voice call
import './styles/VoiceCall.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  id: string;
}

// Direct Supabase configuration
const SUPABASE_URL = 'https://fgovywiapnpjgqoviicz.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZnb3Z5d2lhcG5wamdxb3ZpaWN6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI3OTI4MjgsImV4cCI6MjA1ODM2ODgyOH0.uRxpNAgcjLQGUL1Oq23QfFniPMHtfLX_6cIha4DHUa0';

function App() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Welcome! I can help you explore Canada’s 2025 Innovation Strategy—policies, impact, and what it means for our future.',
      timestamp: Date.now(),
      id: 'welcome'
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isKnowledgeBaseLoaded, setIsKnowledgeBaseLoaded] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'info' | 'images'>('info');
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [directImages, setDirectImages] = useState<any[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({
    status: 'Initializing system...',
    percentage: 10
  });
  
  // New state for voice call
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Check for existing authentication on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('isAuthenticated');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Handle logout
  const handleLogout = () => {
    // Clear authentication
    sessionStorage.removeItem('isAuthenticated');
    setIsAuthenticated(false);
    
    // Reset app state
    setIsKnowledgeBaseLoaded(false);
    setMessages([{
      role: 'assistant',
      content: 'Welcome! I can help you explore Canada’s 2025 Innovation Strategy—policies, impact, and what it means for our future.',
      timestamp: Date.now(),
      id: 'welcome'
    }]);
    setInput('');
    setLoadingProgress({
      status: 'Initializing system...',
      percentage: 10
    });
  };

  // Initialize knowledge base on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      async function initializeData() {
        try {
          // Update loading progress for embedding model initialization
          setLoadingProgress({
            status: 'Initializing embeddings model...',
            percentage: 20
          });
          
          // Wait a moment to show progress
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Update loading progress for loading documents
          setLoadingProgress({
            status: 'Loading documents from Supabase...',
            percentage: 40
          });
          
          // Wait a moment to show progress
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Start loading preprocessed data
          setLoadingProgress({
            status: 'Processing document chunks...',
            percentage: 60
          });
          
          // Load preprocessed data from Supabase
          const success = await loadPreprocessedData();
          
          // Update loading progress for image processing
          setLoadingProgress({
            status: 'Loading and processing images...',
            percentage: 80
          });
          
          // Wait a moment to show progress
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Final setup
          setLoadingProgress({
            status: 'Finalizing knowledge base...',
            percentage: 95
          });
          
          // Wait a moment to show final progress
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Complete loading
          setLoadingProgress({
            status: 'Knowledge base ready!',
            percentage: 100
          });
          
          // Wait a moment to show completion
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Set knowledge base as loaded
          setIsKnowledgeBaseLoaded(success);
          console.log('Knowledge base loaded:', success);
        } catch (error) {
          console.error('Error loading knowledge base:', error);
          setLoadingProgress({
            status: 'Error loading knowledge base. Please refresh.',
            percentage: 100
          });
        }
      }
      
      initializeData();
    }
  }, [isAuthenticated]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when messenger is loaded
  useEffect(() => {
    if (isAuthenticated && isKnowledgeBaseLoaded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAuthenticated, isKnowledgeBaseLoaded]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const userInput = input.trim();
    if (!userInput) return;

    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: userInput,
      timestamp: Date.now(),
      id: `user-${Date.now()}`
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      if (!isKnowledgeBaseLoaded) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I'm still loading the knowledge base. Please wait a moment and try again.",
          timestamp: Date.now(),
          id: `loading-${Date.now()}`
        }]);
        setIsLoading(false);
        return;
      }

      // Search for relevant context
      const relevantContext = await searchDocuments(userInput);
      
      // Call API with context
      let response = await fetchChatResponse(userInput, relevantContext);
      
      // Ensure response is a string to avoid TypeScript errors
      if (!response) {
        response = "Sorry, I couldn't generate a response. Please try again.";
      }
      
      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        timestamp: Date.now(),
        id: `assistant-${Date.now()}`
      }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again later.',
        timestamp: Date.now(),
        id: `error-${Date.now()}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Format timestamp
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      
      // Load vector store data
      await loadPreprocessedData();
      
      // Directly load images from Supabase to ensure they're available
      try {
        const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
        const { data: images, error } = await supabase.from('images').select('*');
        
        if (error) {
          console.error('Error loading images directly:', error);
        } else if (images && images.length > 0) {
          console.log(`Directly loaded ${images.length} images from Supabase`);
          setDirectImages(images);
          
          // Populate extractedImages map
          for (const image of images) {
            extractedImages.set(image.image_id, {
              dataUrl: image.imgbb_url,
              pageNumber: image.page_number,
              description: image.description
            });
          }
          
          console.log('Populated extractedImages map with', extractedImages.size, 'images');
        }
      } catch (err) {
        console.error('Exception loading images:', err);
      }
      
      setIsLoading(false);
      setIsKnowledgeBaseLoaded(true);
    }
    
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  // Fixed handleImageClick function
  const handleImageClick = async (image: any) => {
    if (!isKnowledgeBaseLoaded || isLoading) return;
    
    // Create a user message with the proper FIGURE_ID format
    const imageQuery = `Tell me about this figure: [FIGURE_ID:${image.image_id}]`;
    
    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: imageQuery,
      timestamp: Date.now(),
      id: `user-${Date.now()}`
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Search for relevant context
      const relevantContext = await searchDocuments(imageQuery);
      
      // Call API with the standard context format (don't modify the structure)
      let response = await fetchChatResponse(imageQuery, relevantContext);
      
      // If the response doesn't already include the figure ID, add it
      if (!response.includes(`[FIGURE_ID:${image.image_id}]`)) {
        response = `${response}\n\n[FIGURE_ID:${image.image_id}]`;
      }
      
      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        timestamp: Date.now(),
        id: `assistant-${Date.now()}`
      }]);
    } catch (error) {
      console.error('Error generating response for image:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error when analyzing this image. Please try again.',
        timestamp: Date.now(),
        id: `error-${Date.now()}`
      }]);
    } finally {
      setIsLoading(false);
      // Close sidebar on mobile after selecting an image
      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      }
    }
  };

  // Add handlers for voice call
  const handleStartVoiceCall = () => {
    setShowVoiceCall(true);
  };

  const handleEndVoiceCall = () => {
    setShowVoiceCall(false);
  };

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // If authenticated but knowledge base is not loaded, show loading screen
  if (!isKnowledgeBaseLoaded) {
    return <LoadingScreen progress={loadingProgress} />;
  }

  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Canada Innovation</h2>
          <button className="close-btn" onClick={() => setIsSidebarOpen(false)}>×</button>
        </div>
        
        <div className="sidebar-tabs">
          <button 
            className={activeTab === 'info' ? 'active' : ''} 
            onClick={() => setActiveTab('info')}
          >
            <IoInformation /> Info
          </button>
          <button 
            className={activeTab === 'documents' ? 'active' : ''} 
            onClick={() => setActiveTab('documents')}
          >
            <IoDocumentAttach /> Upload
          </button>
          <button 
            className={activeTab === 'images' ? 'active' : ''} 
            onClick={() => setActiveTab('images')}
          >
            <IoImages /> Images
          </button>
        </div>
        
        <div className="sidebar-content">
          {activeTab === 'documents' && (
            <PDFUploader onUploadComplete={() => setIsSidebarOpen(false)} />
          )}
          
          {activeTab === 'info' && (
            <div className="info-section">
              <div className="kb-status">
                <div className={`status-indicator ${isKnowledgeBaseLoaded ? 'loaded' : 'loading'}`}></div>
                <span>{isKnowledgeBaseLoaded ? 'Knowledge base loaded' : 'Loading knowledge base...'}</span>
              </div>
              
              <div className="info-card">
                <h3>About This Chatbot</h3>
                <p>This AI assistant is designed to provide information about Canada's Innovation Strategy and Budget 2024 initiatives.</p>
                <p>The chatbot has been pre-loaded with relevant documents and can reference specific charts and figures.</p>
              </div>
              
              <h3>Example Questions</h3>
              <div className="example-questions">
                <button onClick={() => {
                  setInput("What are the key innovation factors in your proposal?");
                  inputRef.current?.focus();
                }}>
                  What are the key innovation factors in your proposal?
                </button>
                
        
                <button onClick={() => {
                  setInput("Explain me your policies with figures and graphs?");
                  inputRef.current?.focus();
                }}>
                  Explain me your policies with figures and graphs?
                </button>
                
                <button onClick={() => {
                  setInput("Can you show me figures about R&D investment in Canada?");
                  inputRef.current?.focus();
                }}>
                  Can you show me figures about R&D investment in Canada?
                </button>
              </div>
              
              <div className="logout-section">
                <button 
                  className="logout-button-sidebar" 
                  onClick={handleLogout}
                >
                  <IoLogOut /> Logout
                </button>
              </div>
            </div>
          )}
          
          {activeTab === 'images' && (
            <div className="images-gallery">
              <h3>Available Figures & Charts</h3>
              <p className="images-info">These figures are from the Canada Innovation Strategy documents and can be referenced in your questions. Click any image to ask about it.</p>
              
              <div className="images-grid">
                {directImages.map((image, index) => (
                  <div 
                    className="image-card" 
                    key={image.image_id}
                    onClick={() => handleImageClick(image)}
                  >
                    <div className="image-container">
                      <img 
                        src={image.imgbb_url} 
                        alt={image.description || `Figure ${index + 1}`} 
                        loading="lazy"
                      />
                    </div>
                    <div className="image-info">
                      <span className="image-page">Page {image.page_number}</span>
                      <p className="image-desc">{image.description || "Chart from Canada Innovation Strategy"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Chat Area */}
      <div className="main-content">
        <header className="chat-header">
          <button 
            className="sidebar-toggle" 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <IoMenuSharp />
          </button>
          <h1>Canada Innovation Strategy</h1>
          
          {/* Logout Button */}
          <button 
            className="logout-button" 
            onClick={handleLogout}
            aria-label="Logout"
          >
            <IoLogOut />
          </button>
        </header>
        
        <div className="messages-container">
          <div className="messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.role}`}>
                <div className="message-content">
                  {message.role === 'assistant' ? (
                    <MessageContent content={message.content} />
                  ) : (
                    <div className="message-text">{message.content}</div>
                  )}
                  <div className="message-time">{formatTime(message.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} style={{ height: "140px", visibility: "hidden" }}></div>
          </div>
        </div>
        
        <div className="chat-input-container">
          <form className="message-input" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Canada's innovation strategy..."
              onKeyDown={handleKeyPress}
              rows={1}
            />
            <div className="input-buttons">
              <button
                type="button"
                className="voice-button"
                onClick={handleStartVoiceCall}
                aria-label="Start voice call"
              >
                <IoMic />
              </button>
              <button 
                type="submit" 
                className="send-button"
                disabled={isLoading || !input.trim()}
              >
                <IoSend />
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Voice Call Overlay */}
      <VoiceCallOverlay 
        visible={showVoiceCall} 
        onClose={handleEndVoiceCall} 
      />
    </div>
  );
}

export default App;
